import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Input, CUSTOM_ELEMENTS_SCHEMA, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SmallHeaderComponent } from '../../shared/partials/small-header/small-header.component';
import { DestinationsService } from '../../reducers/services/destinations.service';
import { SectionTitleComponent } from '../../shared/components/ui-elements/section-title/section-title.component';
import { PdfViewerComponent } from '../../shared/components/pdf-viewer/pdf-viewer.component';
import { ViewportService } from '../services/viewport.service';
import { ReserveTablePopupComponent } from '../reserve-table-popup/reserve-table-popup.component';
import { TakeawayPopupComponent } from '../takeaway-popup/takeaway-popup.component';
import { Dish, DishService } from '../services/dish.service';
import { MenuPdfService } from './menu-pdf.service';
import { AlertService } from '../services/alert.service';

@Component({
  selector: 'app-dining-details-page',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    SmallHeaderComponent,
    SectionTitleComponent,
    PdfViewerComponent,
    ReserveTablePopupComponent,
    TakeawayPopupComponent,
  ],
  templateUrl: './dining-details-page.component.html',
  styleUrl: './dining-details-page.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DiningDetailsPageComponent {
  diningData: any;
  isTocExpanded = false;
  totalCount: number = 0;
  slug: string = '';
  page = 1;
  limit = 9;
  blogContainerId = 'blog-search-container';
  query = '';
  upcominghotels: any[] = [];
  tabs: any[] = [];
  safeMapUrl: SafeResourceUrl | null = null;
  diningSlug: string = '';
  dishes: Dish[] = [];

  downloadingMenuPdf = false;

  showReservePopup = false;
  showTakeawayPopup = false;

  private platformId = inject(PLATFORM_ID);
  destinations: any[] = [];
  loading = false;
  mobileView = false;
  openingTime: string = '';
  closingTime: string = '';
  gst: number = 0;

  @Input() data: any;

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute,
    private destinationService: DestinationsService,
    private viewportService: ViewportService,
    private dishService: DishService,
    private menuPdfService: MenuPdfService,
    private alertService: AlertService
  ) { }

  ngOnInit(): void {
    this.diningSlug = this.route.snapshot.paramMap.get('slug') || '';

    if (isPlatformBrowser(this.platformId)) {
      this.getDestinations();
      this.fetchDiningsData();

      this.viewportService.getViewportWidth().subscribe((width: number) => {
        this.mobileView = width < 750;
      });

      this.scrollToTop();
    }
  }

  scrollToTop(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  fetchDishes(restaurantId: string): void {
    this.dishService.getDishes(restaurantId).subscribe((dishes: Dish[]) => {
      this.dishes = dishes;
    });
  }

  fetchDiningsData(): void {
    this.destinationService
      .getDiningDescriptionData(this.diningSlug)
      .subscribe((res: any) => {
        this.diningData = res;

        this.openingTime = res?.openingTime || '';
        this.closingTime = res?.closingTime || '';
        this.gst = Number(res?.gst || 0);

        const raw = res?.mapLink || '';
        this.safeMapUrl = this.toSafeEmbed(raw);

        if (this.diningData?._id) {
          this.fetchDishes(this.diningData._id);
        }
      });
  }

  getDestinations(): void {
    this.loading = true;
    this.destinationService
      .getDestinationDropDown({ country: 'India' })
      .subscribe({
        next: (res: any) => {
          this.destinations = res?.data;
          this.loading = false;
          this.tabs = [
            ...this.destinations?.map((d: any) => ({
              label: d.name,
              target: `#${d.slug}-section`,
            })),
          ];
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  private toSafeEmbed(raw: string): SafeResourceUrl | null {
    const embedUrl = this.toGoogleEmbedUrl(raw || '');
    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  toGoogleEmbedUrl(raw: string): string {
    if (!raw) return '';

    let value = raw.trim();

    if (value.startsWith('<iframe')) {
      const srcMatch =
        value.match(/src\s*=\s*"([^"]+)"/i) ||
        value.match(/src\s*=\s*'([^']+)'/i);

      if (srcMatch?.[1]) value = srcMatch[1].trim();
    }

    try {
      const u = new URL(value);

      if (!u.hostname.includes('google.')) return value;
      if (u.pathname.includes('/maps/embed') && u.searchParams.has('pb')) {
        const pb = u.searchParams.get('pb') || '';
        const m =
          pb.match(/0x[0-9a-fA-F]+:0x([0-9a-fA-F]+)/) ||
          pb.match(/0x[0-9a-fA-F]+%3A0x([0-9a-fA-F]+)/);

        if (m?.[1]) {
          const cidDec = BigInt('0x' + m[1]).toString(10);

          const out = new URL('https://www.google.com/maps');
          out.searchParams.set('cid', cidDec);
          out.searchParams.set('output', 'embed');
          out.searchParams.set('mapclient', 'embed');
          out.searchParams.set('hl', 'en-GB');
          out.searchParams.set('gl', 'US');

          return out.toString();
        }
        return value;
      }

      if (u.pathname === '/maps' || u.pathname.startsWith('/maps')) {
        u.searchParams.set('output', 'embed');
        u.searchParams.set('mapclient', 'embed');
        return u.toString();
      }

      return value;
    } catch {
      
      return `https://www.google.com/maps?q=${encodeURIComponent(value)}&output=embed`;
    }
  }

  openReservePopup(): void {
    this.showReservePopup = true;
  }

  closeReservePopup(): void {
    this.showReservePopup = false;
  }

  openTakeawayPopup(): void {
    if (!this.isRestaurantOpen()) {
      this.alertService.swalWarning({
        title: 'This restaurant is closed now',
        text: `Take away is available from ${this.openingTime} to ${this.closingTime}.`,
        confirmButtonText: 'Okay',
        showCancelButton: false,
      });
      return;
    }
    this.showTakeawayPopup = true;
  }

  closeTakeawayPopup(): void {
    this.showTakeawayPopup = false;
  }

  handleReservation(res: any): void {
    alert(`Reservation confirmed for ${res.persons} people on ${res.date} at ${res.timeSlot}`);
    this.closeReservePopup();
  }

  handleOrder(order: any): void {
    alert('Your takeaway order has been placed successfully!');
    this.closeTakeawayPopup();
  }

  downloadMenuPdf(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const pdfUrl =
      'https://udaan-docs.s3.ap-south-1.amazonaws.com/pdf/grains-cookbook.pdf';

    window.open(pdfUrl, '_blank');
  }

    private isRestaurantOpen(): boolean {
    if (!this.openingTime || !this.closingTime) return true;

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const openMinutes = this.convertTimeToMinutes(this.openingTime);
    const closeMinutes = this.convertTimeToMinutes(this.closingTime);

    if (openMinutes === null || closeMinutes === null) return true;

    // Same day timing, example: 7:30 AM - 10:30 PM
    if (openMinutes < closeMinutes) {
      return currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
    }

    // Overnight timing, example: 8:00 PM - 2:00 AM
    return currentMinutes >= openMinutes || currentMinutes <= closeMinutes;
  }

  private convertTimeToMinutes(time: string): number | null {
    const match = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

    if (!match) return null;

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridian = match[3].toUpperCase();

    if (meridian === 'PM' && hours !== 12) hours += 12;
    if (meridian === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  get showTakeaway(): boolean {
    return this.diningData?.enableTakeaway !== false;
  }

}
